'use client';
import { cn } from '@core/utils';
import { useSuspenseQuery } from '@tanstack/react-query';
import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import { motion } from 'framer-motion';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { UnauthQueryOptions } from '@/api';
import { Image } from '@/components/client';

const RegionErrorFallback = () => {
  return (
    <section className="w-full bg-Gray-black py-80">
      <div className="flex flex-col gap-32 px-32">
        <h2 className="font-800 text-Gray-white typo-h4">참여 지역 소개</h2>
        <div className="flex h-200 w-full flex-col items-center justify-center rounded-large bg-Gray-900">
          <div className="flex flex-col items-center gap-12 px-24 text-center">
            <svg
              width="48"
              height="48"
              viewBox="0 0 48 48"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <circle cx="24" cy="24" r="20" fill="#374151" />
              <path
                d="M24 14V26M24 32V34"
                stroke="#9CA3AF"
                strokeWidth="3"
                strokeLinecap="round"
              />
            </svg>
            <p className="font-500 text-Gray-550 typo-body4">
              참여 지역 정보를 불러올 수 없습니다
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

const RegionSectionContent = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const autoplayRef = useRef(
    Autoplay({ delay: 1_500, stopOnInteraction: false }),
  );

  const { data } = useSuspenseQuery({
    ...UnauthQueryOptions[
      'get:/wello/v2/allows/local-festa/institution-specialty'
    ](),
    select: ({ context }) => context,
  });

  const regions = data?.institutionSpecialtyList || [];

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      align: 'center',
      dragFree: false,
      containScroll: 'trimSnaps',
    },
    [autoplayRef.current],
  );

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setActiveIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);

    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi, onSelect]);

  const handleMouseEnter = useCallback(() => {
    if (autoplayRef.current) {
      autoplayRef.current.stop();
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (autoplayRef.current) {
      autoplayRef.current.play();
    }
  }, []);

  return (
    <section className="w-full bg-Gray-black py-80">
      <motion.div
        className="flex flex-col gap-52 px-32"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.15, ease: 'easeOut' }}
      >
        <h2 className="font-800 text-Gray-white typo-h4">참여 지역 소개</h2>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.15, ease: 'easeOut' }}
      >
        <div
          className="overflow-hidden p-32"
          ref={emblaRef}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div className="flex">
            {regions.map((region, index) => {
              const isActive = index === activeIndex;
              const bgColor = (() => {
                if (!isActive) return 'bg-Gray-900';
                if (activeIndex % 2 === 0) return 'bg-B-500';

                return 'bg-[#FF5C36]';
              })();

              const slideCount = regions.length;
              const isNearLeft =
                index === (activeIndex - 1 + slideCount) % slideCount;
              const isNearRight = index === (activeIndex + 1) % slideCount;
              const isAsideLeft =
                index === (activeIndex - 2 + slideCount) % slideCount;
              const isAsideRight = index === (activeIndex + 2) % slideCount;
              const isHide =
                !isActive &&
                !isNearLeft &&
                !isNearRight &&
                !isAsideLeft &&
                !isAsideRight;

              const zIndex = (() => {
                if (isActive) return 10;
                if (isNearLeft || isNearRight) return 5;
                if (isAsideLeft || isAsideRight) return 3;

                return 1;
              })();

              return (
                <div
                  key={region.institutionId}
                  className="-mr-30 min-w-0 flex-[0_0_170px]"
                  style={{ zIndex }}
                >
                  <article
                    className={cn(
                      'aspect-[170/236] w-full rounded-large p-20 transition-all duration-500 ease-in-out',
                      bgColor,
                      isNearLeft && 'scale-90',
                      isNearRight && 'scale-90',
                      isAsideLeft && 'scale-75',
                      isAsideRight && 'scale-75',
                      isHide && 'scale-50 opacity-0',
                    )}
                  >
                    <div className="flex h-full flex-col items-center justify-between gap-16">
                      <h3 className="font-700 text-Gray-white typo-body1">
                        {region.institutionName}
                      </h3>

                      <div className="size-86">
                        <Image
                          alt={`${region.institutionName} 특산물`}
                          className="size-full object-contain"
                          width={86 * 2}
                          height={86 * 2}
                          src={region.specialtyImageUrl}
                        />
                      </div>

                      <p className="whitespace-pre-wrap text-center font-400 text-Gray-white typo-body4">
                        {region.shortSummary}
                      </p>
                    </div>
                  </article>
                </div>
              );
            })}
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export const RegionSection = () => {
  return (
    <ErrorBoundary fallbackRender={() => <RegionErrorFallback />}>
      <RegionSectionContent />
    </ErrorBoundary>
  );
};
